import cn from 'classnames';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Spinner } from 'components';
import ActionMenu from 'components/ActionMenu';
import { Button } from 'components/form';

import { API_ROUTES } from 'lib/api-routes';
import { ONBOARDING_FLOW_STATE } from 'lib/consts/flowState';
import { TUserApiResponse } from 'lib/models/User.model';
import { CompanyResponse } from 'lib/models/company';
import { InternshipTouchpointable } from 'lib/models/internship';
import { JobTouchpointable } from 'lib/models/job';
import {
  OpportunitiesResponse,
  OpportunityAttributes,
  TouchpointState,
} from 'lib/models/opportunity';
import { TagAttributes, TagType } from 'lib/models/tag';
import { TouchpointType } from 'lib/models/touchpoint';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { apiInstance } from 'lib/utils/axios';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { getOpportunityPublicPagePathname } from 'utils/urls';

import Header from '../Header';
import StepHeading from '../StepHeading';
import {
  TouchpointCard,
  TouchpointCartDataProps,
} from '../TouchpointCard/TouchpointCard.component';
import styles from './Step5.module.scss';

interface Step5Props {
  progress: number;
  setFlowState: (flowState: string) => void;
}

export const Step5: React.FC<Step5Props> = ({ progress, setFlowState }) => {
  const { updateUserApiResponse } = useAuth();

  const router = useRouter();
  const [opportunities, setOpportunities] =
    useState<Array<OpportunityAttributes> | null>(null);
  const [t] = useTranslation('onboarding.recruiter');

  useEffect(() => {
    const opportunitiesPathname = `${API_ROUTES.COMPANY_TOUCHPOINTS}/similar?touchpointable_types[]=Job&touchpointable_types[]=Internship`;
    apiInstance
      .get(opportunitiesPathname)
      .then((res: { data: OpportunitiesResponse }) => {
        const list = parseArrayResponse(res.data);
        setOpportunities(list);
      });
  }, []);

  const handleButton = async (type?: TouchpointType) => {
    if (type) {
      const pathname =
        type === TouchpointType.Event
          ? `${PAGE_ROUTES.CAMPAIGNS_CREATE}?step=1&type=${type}&onboarding=true`
          : `${PAGE_ROUTES.HIRING_CREATE}?step=1&onboarding=true`;
      router.push(pathname);
    } else {
      const flow_state = ONBOARDING_FLOW_STATE.COMPLETE;

      const recruiter = { flow_state };
      const userResponse = await apiInstance.patch<TUserApiResponse>(
        API_ROUTES.RECRUITER_SIGNUP,
        {
          recruiter,
        }
      );
      updateUserApiResponse(userResponse.data);
    }
  };

  const getButton = (top: boolean) => {
    return (
      <ActionMenu
        actionElement={
          <Button
            type="button"
            color="primary"
            className={styles.button}
            isFullWidth
          >
            {t('step-5.create-opportunity')}
            <Icon iconName="icon_arrow-down" className={styles.buttonIcon} />
          </Button>
        }
        triggerButtonClassName={styles.trigger}
        options={[
          {
            id: '1',
            label: t('step-5.job-internship'),
            onClick: () => {
              handleButton(TouchpointType.Job);
            },
          },
          {
            id: '2',
            label: t('step-5.event'),
            onClick: () => handleButton(TouchpointType.Event),
          },
        ]}
        listClassName={cn(styles.list, {
          [styles.listBottom]: top,
          [styles.listTop]: !top,
        })}
      />
    );
  };

  const getCards = useMemo(() => {
    if (opportunities === null) {
      return (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      );
    } else {
      const touchpointCards = opportunities.map((campaign) => {
        const touchpointable = parseResponse(
          campaign.touchpointable as
            | JobTouchpointable
            | InternshipTouchpointable
        );

        const area_of_responsibilities = parseArrayResponse<
          TagAttributes<TagType.AreaOfResponsibility>
        >(campaign.area_of_responsibilities)
          .map((item: { name: string }) => item.name)
          ?.join(', ');

        const company = parseResponse(
          campaign.creatable_for as CompanyResponse
        );

        const opportunity = {
          id: campaign.id,
          title: campaign.title,
          application_deadline: campaign.application_deadline,
          maximum_salary: touchpointable.maximum_salary,
          minimum_salary: touchpointable.minimum_salary,
          pay_currency: campaign.pay_currency,
          touchpointable_type: campaign.touchpointable_type,
          isExpired: campaign.state === TouchpointState.EXPIRED,
          url: `${window.location.origin}${getOpportunityPublicPagePathname(
            campaign.touchpointable_type,
            campaign.slug
          )}`,
          kind: campaign.kind,
          working_model: campaign.working_model,
          area_of_responsibilities: area_of_responsibilities,
          created_at: '',
          company: {
            name: company.name,
            logo: company.icon_url,
            is_verified: company.is_verified,
          },
        } as TouchpointCartDataProps;
        return <TouchpointCard {...opportunity} key={campaign.id} />;
      });
      return (
        <>
          {touchpointCards}
          {getButton(false)}
        </>
      );
    }
  }, [opportunities]);

  return (
    <>
      <Header
        percentComplete={progress}
        onBack={() => {
          setFlowState(ONBOARDING_FLOW_STATE.POST_4);
        }}
      />
      <div className={styles.stepContent}>
        <StepHeading
          title={t('step-5.header.title')}
          desc={t('step-5.header.desc')}
        />

        <div>
          {getButton(true)}
          <Button
            type="button"
            color="secondary"
            className={cn(styles.yesNoButton, styles.button)}
            onClick={() => handleButton()}
            isFullWidth
          >
            {t('do-later')}
          </Button>
        </div>

        <div className={styles.divider}>
          <hr />
        </div>

        <StepHeading desc={t('step-5.header.desc-2')} />

        {getCards}
      </div>
    </>
  );
};
